import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from '@reach/router';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import TablePagination from '@mui/material/TablePagination';
import Close from '@material-ui/icons/Close';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ArrowDropUp from '@material-ui/icons/ArrowDropUp';
import DragIndicator from '@material-ui/icons/DragIndicator';
import FilterList from '@material-ui/icons/FilterList';
import GetApp from '@material-ui/icons/GetApp';
import KeyboardReturn from '@material-ui/icons/KeyboardReturn';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import ViewColumn from '@material-ui/icons/ViewColumn';
import { Box, Flex } from '@rebass/grid';
import PageLayout from '../../page-layout';
import DashboardMemberProfileDrawer from '../components/dashboard-member-profile-drawer';
import ListAvatar from '../../atom/list-avatar';
import { LoadingDots } from '../../atom/loading-dots';
import { colors } from '../../../config/theme';
import {
  fetchMemberExplorerDefinitions,
  runMemberExplorerBulkAction,
  searchMemberExplorerUsers,
} from '../../../lib/fetchers/gyms';

const DEFAULT_COLUMNS = ['name', 'gym_program', 'belt', 'last_checkin_at', 'last_active_date'];
const DEFAULT_SORT = { field: 'name', direction: 'asc' };

const FIELD_CONFIG = {
  name: { label: 'Member name', operators: [{ value: 'contains', label: 'contains' }], type: 'text' },
  gym_program_id: { label: 'Program', operators: [{ value: 'eq', label: 'is' }, { value: 'neq', label: 'is not' }, { value: 'is_empty', label: 'has no value' }], type: 'programs' },
  belt_id: { label: 'Belt', operators: [{ value: 'eq', label: 'is' }, { value: 'neq', label: 'is not' }], type: 'belts' },
  belt_size_id: { label: 'Belt size', operators: [{ value: 'eq', label: 'is' }, { value: 'neq', label: 'is not' }], type: 'belt_sizes' },
  has_parent: { label: 'Has parent', operators: [{ value: 'eq', label: 'is' }], type: 'boolean' },
  has_children: { label: 'Has child', operators: [{ value: 'eq', label: 'is' }], type: 'boolean' },
  membership_activity: { label: 'Membership activity', operators: [{ value: 'active', label: 'is active' }, { value: 'inactive_for_months', label: 'is inactive for' }], type: 'activity' },
  age: { label: 'Age', operators: [{ value: 'adult', label: 'is adult' }, { value: 'minor', label: 'is minor' }, { value: 'at_least', label: 'is at least' }, { value: 'at_most', label: 'is at most' }], type: 'number' },
  last_checkin_at: { label: 'Last check-in', operators: [{ value: 'before', label: 'is before' }, { value: 'after', label: 'is after' }, { value: 'is_empty', label: 'has no value' }], type: 'date' },
  start_date: { label: 'Started training', operators: [{ value: 'before', label: 'is before' }, { value: 'after', label: 'is after' }], type: 'date' },
  last_promotion_date: { label: 'Last promotion', operators: [{ value: 'before', label: 'is before' }, { value: 'after', label: 'is after' }, { value: 'is_empty', label: 'has no value' }], type: 'date' },
  active_days_since_last_promotion: { label: 'Active days since last promotion', operators: [{ value: 'at_least', label: 'is at least' }, { value: 'at_most', label: 'is at most' }], type: 'number' },
};

const COLUMN_LABELS = {
  name: 'Name',
  gym_program: 'Program',
  belt: 'Belt',
  belt_size: 'Belt size',
  start_date: 'Start date',
  last_checkin_at: 'Last check-in',
  last_active_date: 'Last active date',
  last_promotion_date: 'Last promotion',
  active_days_since_last_promotion: 'Active days since last promotion',
  parent: 'Parent / guardian',
  membership_activity: 'Membership activity',
};

const Surface = styled(Card)`
  /* Material UI's Paper/Card default hides overflow. The query menu must be
     allowed to extend below this card and above the results card. */
  overflow: visible !important;
  position: relative;
  z-index: 2;
`;

const Builder = styled.div`
  position: relative;
  z-index: 3;
`;

const BuilderRow = styled.div`
  min-height: 48px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border: 1px solid ${colors.borderMuted};
  border-radius: 8px;
  background: ${colors.surface};
`;

const QueryInput = styled(TextField)`
  min-width: 210px;
  flex: 1 1 210px;
`;

const ViewControl = styled.div`
  margin-left: auto;
  flex: 0 0 auto;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${colors.primary};
  color: #fff !important;
  font-size: 13px;

  .MuiIconButton-root { color: #fff !important; }
`;

const Suggestions = styled.div`
  width: min(440px, calc(100vw - 32px));
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid ${colors.borderMuted};
  border-radius: 8px;
  background: ${colors.surface};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const PendingField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 10px;
  border-radius: 999px;
  background: ${colors.primary};
  color: #fff !important;
  font-size: 13px;
  white-space: nowrap;

  .MuiIconButton-root { color: #fff !important; }
`;

const SuggestionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  color: ${colors.textPrimary};
  text-align: left;
  cursor: pointer;

  background: ${({ active }) => (active ? colors.surfaceSunken : 'transparent')};

  &:hover, &:focus { background: ${colors.surfaceSunken}; }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
  th, td { padding: 12px 16px; border-bottom: 1px solid ${colors.borderMuted}; text-align: left; }
  th { background: ${colors.surfaceSunken}; white-space: nowrap; }
  th:first-child, td:first-child { width: 44px; padding: 8px; text-align: center; }
  tbody tr { cursor: pointer; }
  tbody tr:hover { background: ${colors.surfaceSunken}; }
`;

const SortHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: -8px;
  padding: 8px;
  border: 0;
  background: transparent;
  color: ${colors.textPrimary};
  cursor: pointer;
  font: inherit;
  font-weight: inherit;
  white-space: nowrap;

  &:hover, &:focus { text-decoration: underline; }
`;

const SortArrows = ({ active, direction }) => (
  <Box width="20px" height="24px" style={{ display: 'inline-flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 0 }}>
    <ArrowDropUp fontSize="small" style={{ marginBottom: -9, opacity: active && direction === 'asc' ? 1 : 0.35 }} />
    <ArrowDropDown fontSize="small" style={{ marginTop: -9, opacity: active && direction === 'desc' ? 1 : 0.35 }} />
  </Box>
);

const MemberButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${colors.textPrimary};
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:hover { text-decoration: underline; }
`;

const BulkHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ViewPanel = styled.div`
  width: 300px;
  padding: 16px;
`;

const ColumnOption = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
`;

const storageKey = (gymUid) => `member-explorer-view-v1:${gymUid}`;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
};

const csvCell = (value) => {
  const text = value === null || typeof value === 'undefined' ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const filterLabel = (filter, definitions) => {
  const config = FIELD_CONFIG[filter.field];
  const operator = config.operators.find((item) => item.value === filter.operator);
  let value = filter.value;
  if (config.type === 'programs' || config.type === 'belts' || config.type === 'belt_sizes') {
    const option = (definitions.options?.[config.type] || []).find((item) => String(item.id) === String(value));
    value = option ? option.name : value;
  }
  if (filter.operator === 'is_empty') value = '';
  if (filter.operator === 'inactive_for_months') value = `${value} ${value === 1 ? 'month' : 'months'}`;
  if (config.type === 'boolean') value = value === true || value === 'true' ? 'Yes' : 'No';
  return `${config.label} ${operator.label}${value === '' ? '' : ` ${value}`}`;
};

export const MemberExplorer = () => {
  const { gymUid } = useParams();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [categoryNavigationActive, setCategoryNavigationActive] = useState(false);
  const [queryAnchor, setQueryAnchor] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [filters, setFilters] = useState([]);
  const [viewAnchor, setViewAnchor] = useState(null);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [memberDrawerStack, setMemberDrawerStack] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [priorityUserUids, setPriorityUserUids] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkConfirmation, setBulkConfirmation] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [view, setView] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem(storageKey(gymUid))) || {
        visibleColumns: DEFAULT_COLUMNS,
        columnOrder: DEFAULT_COLUMNS,
        sort: DEFAULT_SORT,
      };
    } catch (_) {
      return { visibleColumns: DEFAULT_COLUMNS, columnOrder: DEFAULT_COLUMNS, sort: DEFAULT_SORT };
    }
  });

  const { data: definitions = {}, isLoading: definitionsLoading, error: definitionsError } = useQuery(
    ['member-explorer-definitions', gymUid],
    () => fetchMemberExplorerDefinitions(gymUid),
    { enabled: !!gymUid }
  );

  const effectiveFilters = useMemo(() => (
    selectedField || !draft.trim()
      ? filters
      : [...filters, { field: 'name', operator: 'contains', value: draft.trim() }]
  ), [draft, filters, selectedField]);

  const filterSignature = JSON.stringify(effectiveFilters);

  // Keep the report responsive locally, but wait until editing stops before
  // issuing an Explorer request for text/filter or column-view changes.
  const searchInput = useMemo(() => ({
    filters: effectiveFilters,
    visibleColumns: view.visibleColumns,
    columnOrder: view.columnOrder,
  }), [effectiveFilters, view.columnOrder, view.visibleColumns]);
  const debouncedSearchInput = useDebouncedValue(searchInput, 1000);
  const searchInputSignature = JSON.stringify(searchInput);
  const debouncedSearchInputSignature = JSON.stringify(debouncedSearchInput);
  const searchInputSettled = searchInputSignature === debouncedSearchInputSignature;

  useEffect(() => {
    setCurrentPage(0);
  }, [filterSignature, view.sort.field, view.sort.direction]);

  const payload = useMemo(() => ({
    query: { version: 1, combinator: 'and', filters: debouncedSearchInput.filters },
    page: currentPage + 1,
    per_page: 50,
    sort: view.sort,
    columns: debouncedSearchInput.visibleColumns,
    priority_user_uids: priorityUserUids,
  }), [currentPage, debouncedSearchInput.filters, debouncedSearchInput.visibleColumns, priorityUserUids, view.sort]);

  const { data: results = {}, isLoading: searchLoading, error: searchError } = useQuery(
    ['member-explorer-search', gymUid, JSON.stringify(payload), debouncedSearchInput.columnOrder.join(',')],
    () => searchMemberExplorerUsers(gymUid, payload),
    { enabled: !!gymUid && !definitionsLoading && searchInputSettled }
  );

  // The API puts guided-retry accounts first before pagination. Keep the same
  // order in the currently rendered rows while that request is refreshing so
  // the selected child accounts never appear to jump back into the result set.
  const displayedUsers = useMemo(() => {
    const users = results.users || [];
    const priorityUids = priorityUserUids;
    if (!priorityUids.length) return users;

    const priorityIndexes = new Map(priorityUids.map((uid, index) => [uid, index]));
    return users
      .map((member, index) => ({ member, index }))
      .sort((left, right) => {
        const leftPriority = priorityIndexes.has(left.member.uid) ? priorityIndexes.get(left.member.uid) : priorityUids.length;
        const rightPriority = priorityIndexes.has(right.member.uid) ? priorityIndexes.get(right.member.uid) : priorityUids.length;
        return (leftPriority - rightPriority) || left.index - right.index;
      })
      .map(({ member }) => member);
  }, [priorityUserUids, results.users]);

  const availableFields = Object.keys(FIELD_CONFIG).filter((field) =>
    FIELD_CONFIG[field].label.toLowerCase().includes(draft.toLowerCase())
  );
  const suggestions = availableFields.map((field) => ({ type: 'field', field, label: FIELD_CONFIG[field].label }));

  const persistView = (nextView) => {
    setView(nextView);
    window.localStorage.setItem(storageKey(gymUid), JSON.stringify(nextView));
  };

  const beginField = (field) => {
    setSelectedField(field);
    setSelectedOperator('');
    setSelectedValue('');
    setDraft('');
    setMenuOpen(false);
    setQueryAnchor(null);
    setActiveSuggestionIndex(0);
    setCategoryNavigationActive(false);
  };

  const resetPendingFilter = () => {
    setSelectedField(null);
    setSelectedOperator('');
    setSelectedValue('');
  };

  const addFilter = (valueOverride) => {
    if (!selectedField || !selectedOperator) return;
    const selected = typeof valueOverride === 'undefined' ? selectedValue : valueOverride;
    if (!['is_empty', 'adult', 'minor'].includes(selectedOperator) && `${selected}`.trim() === '') return;
    const value = ['is_empty', 'adult', 'minor'].includes(selectedOperator) ? 1 : selected;
    setFilters([...filters, { field: selectedField, operator: selectedOperator, value }]);
    resetPendingFilter();
    setMenuOpen(false);
    setQueryAnchor(null);
  };

  const selectSuggestion = (suggestion) => {
    if (!suggestion) return;
    if (suggestion.type === 'name') {
      setFilters([...filters, { field: 'name', operator: 'contains', value: draft.trim() }]);
      setDraft('');
      setMenuOpen(false);
      setQueryAnchor(null);
      setActiveSuggestionIndex(0);
      setCategoryNavigationActive(false);
      return;
    }
    beginField(suggestion.field);
  };

  const submitDraft = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!menuOpen) setMenuOpen(true);
      if (suggestions.length) {
        setCategoryNavigationActive(true);
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setActiveSuggestionIndex((index) => (index + delta + suggestions.length) % suggestions.length);
      }
      return;
    }
    if (event.key === 'Enter' && !selectedField) {
      event.preventDefault();
      if (categoryNavigationActive && suggestions[activeSuggestionIndex]) {
        selectSuggestion(suggestions[activeSuggestionIndex]);
      } else {
        setMenuOpen(false);
        setQueryAnchor(null);
      }
      return;
    }
    if (event.key === 'Escape') {
      setMenuOpen(false);
      setQueryAnchor(null);
    }
  };

  const toggleSelected = (member) => {
    if (selectedMemberIds.includes(member.id)) {
      setPriorityUserUids((current) => current.filter((uid) => uid !== member.uid));
    }
    setSelectedMemberIds((current) => (current.includes(member.id) ? current.filter((id) => id !== member.id) : [...current, member.id]));
  };

  const visibleMemberIds = displayedUsers.map((member) => member.id);
  const allVisibleSelected = visibleMemberIds.length > 0 && visibleMemberIds.every((id) => selectedMemberIds.includes(id));
  const toggleAllVisible = () => {
    setSelectedMemberIds((current) => (allVisibleSelected
      ? current.filter((id) => !visibleMemberIds.includes(id))
      : [...new Set([...current, ...visibleMemberIds])]));
  };

  const { mutate: runBulkAction, isLoading: bulkActionLoading, error: bulkActionError } = useMutation(
    () => runMemberExplorerBulkAction(gymUid, {
      bulk_action: bulkAction,
      user_ids: selectedMemberIds,
      permanent_confirmation: bulkConfirmation,
    }),
    {
      onSuccess: (result) => {
        setBulkResult(result);
        setSelectedMemberIds([]);
        setPriorityUserUids([]);
        setBulkAction(null);
        setBulkConfirmation('');
        queryClient.invalidateQueries(['member-explorer-search', gymUid]);
      },
    }
  );

  const selectChildAccountsForDeletion = (childIds, childUids) => {
    const childAccountIds = [...new Set(childIds)];
    const childAccountUids = [...new Set(childUids)];
    setFilters([]);
    setDraft('');
    resetPendingFilter();
    setMenuOpen(false);
    setQueryAnchor(null);
    setCurrentPage(0);
    setSelectedMemberIds(childAccountIds);
    setPriorityUserUids(childAccountUids);
    setBulkResult(null);
  };

  const valueControl = () => {
    if (!selectedField) return null;
    const type = FIELD_CONFIG[selectedField].type;
    if (['is_empty', 'adult', 'minor'].includes(selectedOperator)) return null;
    if (type === 'programs' || type === 'belts' || type === 'belt_sizes') {
      return <Select value={selectedValue} onChange={(event) => addFilter(event.target.value)} displayEmpty>
        <MenuItem value=""><em>Select</em></MenuItem>
        {(definitions.options?.[type] || []).map((option) => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
      </Select>;
    }
    if (!selectedOperator) return null;
    if (type === 'activity') {
      return <Select value={selectedValue} onChange={(event) => addFilter(Number(event.target.value))}>
        {[1, 2, 3, 4, 5, 6].map((month) => <MenuItem key={month} value={month}>{month} {month === 1 ? 'month' : 'months'}</MenuItem>)}
      </Select>;
    }
    if (type === 'boolean') {
      return <Select value={selectedValue} onChange={(event) => addFilter(event.target.value === 'true')} displayEmpty>
        <MenuItem value=""><em>Select</em></MenuItem>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </Select>;
    }
    return <TextField type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'} value={selectedValue} onChange={(event) => setSelectedValue(event.target.value)} inputProps={type === 'number' ? { min: 0 } : {}} />;
  };

  const columnValue = (member, column) => {
    switch (column) {
      case 'name': return member.name;
      case 'gym_program': return member.gym_program?.name || '—';
      case 'belt': return member.belt?.name || '—';
      case 'belt_size': return member.belt_size?.name || '—';
      case 'start_date': return formatDate(member.start_date);
      case 'last_checkin_at': return formatDate(member.last_checkin_at);
      case 'last_active_date': return formatDate(member.last_active_date);
      case 'last_promotion_date': return formatDate(member.last_promotion_date);
      case 'active_days_since_last_promotion': return member.active_days_since_last_promotion === null || typeof member.active_days_since_last_promotion === 'undefined' ? '—' : member.active_days_since_last_promotion;
      case 'parent': return member.parent?.name || '—';
      case 'membership_activity': return member.last_checkin_at ? 'Active history' : 'No check-ins';
      default: return '—';
    }
  };

  const allColumns = definitions.available_columns || Object.keys(COLUMN_LABELS);
  const columnOrder = [...view.columnOrder, ...allColumns.filter((column) => !view.columnOrder.includes(column))];
  const orderedColumns = columnOrder.filter((column) => view.visibleColumns.includes(column));

  const toggleColumn = (column) => {
    if (column === 'name') return;
    const visibleColumns = view.visibleColumns.includes(column)
      ? view.visibleColumns.filter((item) => item !== column)
      : [...view.visibleColumns, column];
    const nextColumnOrder = view.columnOrder.includes(column) ? view.columnOrder : [...view.columnOrder, column];
    persistView({ ...view, visibleColumns, columnOrder: nextColumnOrder });
  };

  const dropColumn = (event, target) => {
    event.preventDefault();
    if (!draggedColumn || draggedColumn === target) return;
    const order = columnOrder.filter((column) => column !== draggedColumn);
    order.splice(order.indexOf(target), 0, draggedColumn);
    persistView({ ...view, columnOrder: order });
    setDraggedColumn(null);
  };

  const toggleSort = (column) => {
    const direction = view.sort.field === column && view.sort.direction === 'asc' ? 'desc' : 'asc';
    persistView({ ...view, sort: { field: column, direction } });
  };

  const sortableHeader = (column) => {
    const label = COLUMN_LABELS[column] || column;
    const sortable = (definitions.available_sorts || ['name']).includes(column);
    if (!sortable) return label;

    const active = view.sort.field === column;
    return <SortHeaderButton
      type="button"
      onClick={() => toggleSort(column)}
      aria-label={`Sort by ${label}${active ? `, currently ${view.sort.direction === 'asc' ? 'ascending' : 'descending'}` : ''}`}
    >
      {label}<SortArrows active={active} direction={view.sort.direction} />
    </SortHeaderButton>;
  };

  const exportCurrentReport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const exportPayload = { ...payload, page: 1, per_page: 100 };
      const firstPage = await searchMemberExplorerUsers(gymUid, exportPayload);
      const totalPages = firstPage.meta?.total_pages || 1;
      const pages = [firstPage];

      for (let page = 2; page <= totalPages; page += 1) {
        pages.push(await searchMemberExplorerUsers(gymUid, { ...exportPayload, page }));
      }

      const members = pages.flatMap((page) => page.users || []);
      const rows = [
        orderedColumns.map((column) => COLUMN_LABELS[column] || column),
        ...members.map((member) => orderedColumns.map((column) => columnValue(member, column))),
      ];
      const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'member-explorer-report.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
    setSelectedMemberIds([]);
    setPriorityUserUids([]);
  };

  const openMemberDrawer = (member) => setMemberDrawerStack([member]);

  const pushRelatedMemberDrawer = (member) => {
    if (!member?.uid) return;
    setMemberDrawerStack((current) => {
      const existingIndex = current.findIndex((openMember) => openMember.uid === member.uid);
      return existingIndex >= 0 ? current.slice(0, existingIndex + 1) : [...current, member];
    });
  };

  const closeMemberDrawer = (index) => {
    setMemberDrawerStack((current) => current.slice(0, index));
  };

  return (
    <PageLayout selected="member-explorer" pageTitle="Member Explorer" dt>
      <Box mt={4}>
        <Typography variant="h5">Member Explorer</Typography>
        <Typography variant="subtitle1">Build a custom member query without changing the current Gym Members page.</Typography>
      </Box>
      <Box mt={4}>
        <Surface>
          <CardContent>
            <Builder>
              <BuilderRow>
                <FilterList fontSize="small" />
                {filters.map((filter, index) => <Chip key={`${filter.field}-${index}`}>
                  {filterLabel(filter, definitions)}
                  <IconButton size="small" aria-label={`Remove ${filterLabel(filter, definitions)}`} onClick={() => setFilters(filters.filter((_, filterIndex) => filterIndex !== index))}><Close fontSize="small" /></IconButton>
                </Chip>)}
                {selectedField ? <>
                  <PendingField>
                    {FIELD_CONFIG[selectedField].label}
                    <IconButton size="small" aria-label={`Cancel ${FIELD_CONFIG[selectedField].label} filter`} onClick={resetPendingFilter}><Close fontSize="small" /></IconButton>
                  </PendingField>
                  <Select displayEmpty value={selectedOperator} onChange={(event) => { setSelectedOperator(event.target.value); setSelectedValue(''); }}>
                    <MenuItem value=""><em>Select condition</em></MenuItem>
                    {FIELD_CONFIG[selectedField].operators.map((operator) => <MenuItem key={operator.value} value={operator.value}>{operator.label}</MenuItem>)}
                  </Select>
                  {valueControl()}
                  <Button color="primary" variant="contained" onClick={() => addFilter()}>
                    Add filter
                  </Button>
                </> : <QueryInput
                  value={draft}
                  onFocus={(event) => { setQueryAnchor(event.currentTarget); setMenuOpen(true); }}
                  onChange={(event) => { setDraft(event.target.value); setQueryAnchor(event.currentTarget); setMenuOpen(true); setActiveSuggestionIndex(0); setCategoryNavigationActive(false); }}
                  onKeyDown={submitDraft}
                  placeholder="Search or add a filter"
                  variant="standard"
                  InputProps={{ disableUnderline: true, inputProps: { 'aria-label': 'Search and filter members' } }}
                  aria-label="Search members by name or add a filter"
                />}
                <ViewControl>
                  <IconButton aria-label="View options" onClick={(event) => setViewAnchor(event.currentTarget)}><ViewColumn /></IconButton>
                </ViewControl>
              </BuilderRow>
              <Popover
                open={menuOpen && !selectedField && suggestions.length > 0}
                anchorEl={queryAnchor}
                onClose={() => { setMenuOpen(false); setQueryAnchor(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
              >
                <Suggestions role="listbox" aria-label="Member filter suggestions">
                  {suggestions.map((suggestion, index) => <SuggestionButton
                    key={suggestion.field || 'name-search'}
                    active={index === activeSuggestionIndex}
                    role="option"
                    aria-selected={index === activeSuggestionIndex}
                    onMouseEnter={() => { setActiveSuggestionIndex(index); setCategoryNavigationActive(true); }}
                    onClick={() => selectSuggestion(suggestion)}
                  >
                    <span>{suggestion.label}</span><KeyboardReturn fontSize="small" />
                  </SuggestionButton>)}
                </Suggestions>
              </Popover>
            </Builder>
          </CardContent>
        </Surface>
      </Box>
      <Popover open={Boolean(viewAnchor)} anchorEl={viewAnchor} onClose={() => setViewAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <ViewPanel>
          <Typography variant="subtitle1">Sort by</Typography>
          <Flex mt={1} style={{ gap: 8 }}>
            <Select value={view.sort.field} onChange={(event) => persistView({ ...view, sort: { ...view.sort, field: event.target.value } })} fullWidth>
              {(definitions.available_sorts || ['name']).map((field) => <MenuItem key={field} value={field}>{COLUMN_LABELS[field] || field}</MenuItem>)}
            </Select>
            <Select value={view.sort.direction} onChange={(event) => persistView({ ...view, sort: { ...view.sort, direction: event.target.value } })}>
              <MenuItem value="asc">A–Z</MenuItem><MenuItem value="desc">Z–A</MenuItem>
            </Select>
          </Flex>
          <Box mt={3}><Typography variant="subtitle1">Columns</Typography></Box>
          {columnOrder.map((column) => <ColumnOption key={column} draggable={column !== 'name'} onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', column); setDraggedColumn(column); }} onDragEnd={() => setDraggedColumn(null)} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }} onDrop={(event) => dropColumn(event, column)}>
            <DragIndicator fontSize="small" style={{ opacity: column === 'name' ? 0.25 : 1 }} />
            <IconButton size="small" aria-label={`${view.visibleColumns.includes(column) ? 'Hide' : 'Show'} ${COLUMN_LABELS[column] || column}`} onClick={() => toggleColumn(column)}>
              {view.visibleColumns.includes(column) ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
            <Typography variant="body2">{COLUMN_LABELS[column] || column}</Typography>
          </ColumnOption>)}
        </ViewPanel>
      </Popover>
      <Box mt={3}>
        <Surface>
          <CardContent>
            <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
              <Typography variant="subtitle1">{results.meta?.total_items || 0} members</Typography>
              <Flex alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <TablePagination
                  component="div"
                  count={results.meta?.total_items || 0}
                  page={currentPage}
                  onPageChange={handlePageChange}
                  rowsPerPage={50}
                  rowsPerPageOptions={[50]}
                  labelRowsPerPage="Members per page"
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetApp />}
                  onClick={exportCurrentReport}
                  disabled={isExporting || searchLoading}
                >
                  {isExporting ? 'Exporting…' : 'Export CSV'}
                </Button>
              </Flex>
            </Flex>
          </CardContent>
          <TableWrap>
            <Table>
              <thead>{selectedMemberIds.length ? <tr><th colSpan={orderedColumns.length + 2}>
                <BulkHeader>
                  <Typography variant="subtitle2">{selectedMemberIds.length} member{selectedMemberIds.length === 1 ? '' : 's'} selected</Typography>
                  <Flex style={{ gap: 8 }}>
                    <Button variant="outlined" onClick={() => { setBulkConfirmation(''); setBulkAction('disable'); }}>Disable</Button>
                    <Button color="secondary" variant="contained" onClick={() => { setBulkConfirmation(''); setBulkAction('delete'); }}>Delete</Button>
                    <Button onClick={() => { setSelectedMemberIds([]); setPriorityUserUids([]); }}>Cancel</Button>
                  </Flex>
                </BulkHeader>
              </th></tr> : <tr><th><Checkbox size="small" checked={allVisibleSelected} indeterminate={selectedMemberIds.length > 0 && !allVisibleSelected} onChange={toggleAllVisible} inputProps={{ 'aria-label': 'Select all visible members' }} /></th>{orderedColumns.map((column) => <th key={column}>{sortableHeader(column)}</th>)}</tr>}</thead>
              <tbody>
                {displayedUsers.map((member) => <tr key={member.id} onClick={() => openMemberDrawer(member)}>
                  <td onClick={(event) => event.stopPropagation()}><Checkbox size="small" checked={selectedMemberIds.includes(member.id)} onChange={() => toggleSelected(member)} inputProps={{ 'aria-label': `Select ${member.name}` }} /></td>
                  {orderedColumns.map((column) => (column === 'name'
                    ? <td key={column}><MemberButton onClick={(event) => { event.stopPropagation(); openMemberDrawer(member); }}><ListAvatar src={member.avatar_url} name={member.name} />{member.name}</MemberButton></td>
                    : <td key={column}>{columnValue(member, column)}</td>))}
                </tr>)}
              </tbody>
            </Table>
          </TableWrap>
          {(definitionsError || searchError) && <Box p={3} color="error.main">{definitionsError?.message || searchError?.message}</Box>}
          {(definitionsLoading || searchLoading) && <Box p={3}><LoadingDots /></Box>}
          {!definitionsLoading && !searchLoading && (results.users || []).length === 0 && <Box p={3}>No members match this query.</Box>}
        </Surface>
      </Box>
      {memberDrawerStack.map((member, index) => <DashboardMemberProfileDrawer
        key={`${member.uid}-${index}`}
        member={member}
        open
        onClose={() => closeMemberDrawer(index)}
        onSelectRelatedUser={pushRelatedMemberDrawer}
        onMemberUpdated={() => queryClient.invalidateQueries(['member-explorer-search', gymUid])}
      />)}
      <Dialog open={Boolean(bulkAction)} onClose={() => !bulkActionLoading && setBulkAction(null)}>
        <DialogTitle>{bulkAction === 'delete' ? `Permanently delete ${selectedMemberIds.length} member${selectedMemberIds.length === 1 ? '' : 's'}?` : `Disable ${selectedMemberIds.length} member${selectedMemberIds.length === 1 ? '' : 's'}?`}</DialogTitle>
        <DialogContent>
          {bulkAction === 'delete' ? <>
            <Typography paragraph>This cannot be undone. It permanently deletes the selected member accounts, their check-ins, profile data, and Firebase Auth logins. Members with child accounts must have those children deleted first.</Typography>
            <Typography variant="body2" paragraph>Type <strong>{`DELETE ${selectedMemberIds.length} MEMBERS`}</strong> to confirm.</Typography>
            <TextField autoFocus fullWidth label="Permanent deletion confirmation" value={bulkConfirmation} onChange={(event) => setBulkConfirmation(event.target.value)} error={Boolean(bulkActionError)} helperText={bulkActionError?.message} />
          </> : <Typography paragraph>Disabling removes the selected member accounts and their children from active gym access. It does not permanently delete their data.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAction(null)} disabled={bulkActionLoading}>Cancel</Button>
          <Button color={bulkAction === 'delete' ? 'secondary' : 'primary'} onClick={() => runBulkAction()} disabled={bulkActionLoading || (bulkAction === 'delete' && bulkConfirmation !== `DELETE ${selectedMemberIds.length} MEMBERS`)}>{bulkAction === 'delete' ? 'Delete permanently' : 'Disable members'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(bulkResult)}
        onClose={() => setBulkResult(null)}
        PaperProps={{ style: { alignSelf: 'flex-start', marginTop: 64 } }}
      >
        <DialogTitle>{(bulkResult?.results || []).some((result) => result.status === 'failed') ? 'Some accounts need attention' : 'Bulk action complete'}</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {bulkResult?.action === 'delete'
              ? `${bulkResult.deleted_count || 0} member${bulkResult.deleted_count === 1 ? '' : 's'} permanently deleted.`
              : `${bulkResult?.disabled_count || 0} member${bulkResult?.disabled_count === 1 ? '' : 's'} disabled.`}
          </Typography>
          {(bulkResult?.results || []).filter((result) => result.status !== (bulkResult?.action === 'delete' ? 'deleted' : 'disabled')).map((result) => (
            <Box key={result.uid} mb={2}>
              <Typography variant="body2" paragraph>
                {result.name ? `${result.name}: ` : ''}{result.error || 'This member was not changed.'}
              </Typography>
              {result.child_uids?.length > 0 && <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => selectChildAccountsForDeletion(result.child_ids || [], result.child_uids || [])}
              >
                Select {result.child_uids.length} child account{result.child_uids.length === 1 ? '' : 's'} for deletion
              </Button>}
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button color="primary" onClick={() => setBulkResult(null)}>Done</Button></DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default MemberExplorer;
